/**
 * Modal de **asignación de un nuevo talonario** (`POST /api/v1/talonarios/assign`).
 *
 * Replica el modal del SSR `supervisor_talonarios` (`progress/
 * explore_talonarios_ssr.md`, §3.1): trabajador (prellenado), número inicial y
 * final (autocalcula +49 al editar el inicio). Valida con Zod (`assignTalonarioSchema`,
 * rango = 50) antes de delegar; al enviar llama a `useAssignTalonario` (mutación
 * que invalida los talonarios). El error de la API se traduce a mensaje legible.
 *
 * Controlado por `worker` del padre (`null` = cerrado). Presentacional +
 * orquestación de un hook de datos: no hace `fetch`. Usa `components/ui`.
 */
import { useEffect, useState } from "react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
} from "@/components/ui";

import { TALONARIO_SIZE } from "../constants";
import { talonarioErrorMessage } from "../format";
import { useAssignTalonario } from "../hooks/useAssignTalonario";
import { assignTalonarioSchema } from "../schemas";

interface AssignTalonarioDialogProps {
  /** Trabajador al que se asigna; `null` mantiene el modal cerrado. */
  worker: string | null;
  /** Rango actual del trabajador (para mostrarlo como referencia), si se conoce. */
  currentRange?: string | undefined;
  onClose: () => void;
}

/** Errores por campo (de Zod). */
type FieldErrors = Partial<Record<"new_start" | "new_end", string>>;

export function AssignTalonarioDialog({
  worker,
  currentRange,
  onClose,
}: AssignTalonarioDialogProps) {
  const assign = useAssignTalonario();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // Al abrir/cambiar de trabajador, limpia el formulario y el estado de la mutación.
  useEffect(() => {
    if (worker !== null) {
      setStart("");
      setEnd("");
      setFieldErrors({});
      assign.reset();
    }
    // `assign` es estable por render de TanStack Query; solo nos interesa `worker`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worker]);

  /** Al teclear el inicio, autocalcula el fin (+49) para un rango de 50. */
  function handleStartChange(value: string) {
    setStart(value);
    if (/^\d+$/.test(value.trim())) {
      const startNum = Number(value.trim());
      setEnd(String(startNum + TALONARIO_SIZE - 1));
    }
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (worker === null) {
      return;
    }
    const result = assignTalonarioSchema.safeParse({
      worker,
      new_start: start,
      new_end: end,
    });
    if (!result.success) {
      const flattened = result.error.flatten().fieldErrors;
      const errors: FieldErrors = {};
      const startError = flattened.new_start?.[0];
      const endError = flattened.new_end?.[0];
      if (startError) {
        errors.new_start = startError;
      }
      if (endError) {
        errors.new_end = endError;
      }
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    assign.mutate(
      {
        worker,
        new_start: Number(result.data.new_start),
        new_end: Number(result.data.new_end),
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  }

  const apiError = assign.isError ? talonarioErrorMessage(assign.error) : null;

  return (
    <Dialog
      open={worker !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogTitle>Asignar nuevo talonario</DialogTitle>
        {/* Radix vincula `DialogDescription` al diálogo por su id interno (sin
            `aria-describedby` manual). Solo se monta con el modal abierto
            (`worker !== null`), así que nunca queda vacía. */}
        <DialogDescription>
          Trabajador: {worker ?? ""}
          {currentRange ? ` · Rango actual: ${currentRange}` : ""}
        </DialogDescription>

        <form className="mt-4 flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-1">
            <label htmlFor="assign-start" className="text-sm font-medium text-slate-700">
              Número inicial
            </label>
            <Input
              id="assign-start"
              inputMode="numeric"
              autoComplete="off"
              value={start}
              aria-invalid={fieldErrors.new_start ? true : undefined}
              aria-describedby={fieldErrors.new_start ? "assign-start-error" : undefined}
              onChange={(event) => handleStartChange(event.target.value)}
            />
            {fieldErrors.new_start && (
              <p id="assign-start-error" className="text-sm text-red-600">
                {fieldErrors.new_start}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="assign-end" className="text-sm font-medium text-slate-700">
              Número final
            </label>
            <Input
              id="assign-end"
              inputMode="numeric"
              autoComplete="off"
              value={end}
              aria-invalid={fieldErrors.new_end ? true : undefined}
              aria-describedby={fieldErrors.new_end ? "assign-end-error" : undefined}
              onChange={(event) => setEnd(event.target.value)}
            />
            {fieldErrors.new_end && (
              <p id="assign-end-error" className="text-sm text-red-600">
                {fieldErrors.new_end}
              </p>
            )}
          </div>

          <p className="text-xs text-slate-500">
            Un talonario abarca {TALONARIO_SIZE} números consecutivos (el fin se
            calcula al escribir el inicio).
          </p>

          {apiError && (
            <p role="alert" className="text-sm text-red-600">
              {apiError}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={assign.isPending}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={assign.isPending}>
              {assign.isPending ? "Asignando…" : "Asignar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
